// §2A step 13 — Parent results. After the kid finishes the diagnostic,
// show the parent: starting placement (with the "why") and one-tap setup of
// the weekly recap email. Completing this flips onboarding_state to COMPLETE
// and routes to /play (or /parent with a pending claim code, MA-580).
//
// MA-856 (PUX-5, D-PUX-4) — the screen opens on a "Grown-up, take the device
// back" hand-back step: the diagnostic before it is child-held, so the
// parent-facing results (and the recap opt-in) appear only after a 1.5 s hold
// on the same grownupGate machine the kid-tab ⚙ uses. The step is an adult
// speed bump, not a child-facing screen, so it is not narrated, and it adds no
// consent wording (MA-20 is referenced, not designed around). The inert
// Easy / Adaptive / Hard "Starting level" picker is gone (it wrote
// difficultyPreference, which nothing serves from), and the finish button now
// names where it goes.

import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAccount, useKids } from "@/lib/account";
import {
  recommendStartingSkill,
  skillLabel,
  placementFromStoredResult,
} from "@/lib/engine/parentPriors";
import { useAppState, activeChildAge } from "@/App/AppStateContext.tsx";
// MA-80: the ONE age source (progress age, else the registry kids-row age).
import { resolveSelectionAge } from "@/lib/engine/useTodaysMission";
import { BootGate } from "@/components/BootGate";
import { setEmailWeeklyRecap } from "@/supabaseClient.js";
// MA-580 (A) — a parent who arrived via /claim/:code must land on the parent
// dash, not /play; PendingClaimCodeCard is the only consumer of that code.
import { readPendingClaimCode } from "@/lib/teacher/pendingClaimCode";
// MA-889 (D-PUX-28) — the starting skill in the words the child sees on the
// Journey (the same map WorkingOnCard / WhereNowCard use).
import { KID_LABEL_MAP } from "@/lib/engine/journeyView";
// MA-856 — the hand-back hold reuses the kid-tab ⚙ hook as-is (same pure
// grownupGate machine, same HOLD_DURATION_MS, pointer + keyboard hold).
import { useHoldToOpen } from "@/components/GrownupMenu";

export const Route = createFileRoute("/onboarding/parent-results")({
  head: () => ({ meta: [{ title: "PencilWise · Starting point" }] }),
  component: () => (
    <BootGate surface="kid">
      <ParentResults />
    </BootGate>
  ),
});

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";
// MA-856 RED S1 — the hold button keeps bg-primary while pressed (no
// active:bg-primary-pressed), so the bg-primary-pressed progress fill stays
// visible against it for the whole hold.
const HOLD_CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:shadow-none";

function ParentResults() {
  const navigate = useNavigate();
  const app = useAppState();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const kid = kids[kids.length - 1] ?? null;

  // T1.1a: "Based on what you told us" → start the child at the
  // priors-derived skill (the abilities step). No lastAttemptedSkill here —
  // this screen reflects what the PARENT reported, not diagnostic attempts.
  // Track I.1.b: bias the displayed start by the diagnostic placement seed so
  // displayed === served (useTodaysMission applies the same bias). Onboarding
  // diagnostic is math → strand "math".
  // `total` rides along so a skipped/empty diagnostic (total 0 → accuracy 0)
  // reads as no evidence — applyPlacementBias no-ops without it.
  const pr = app?.progress?.placementResults as { accuracy?: number; total?: number } | null;
  const placement = placementFromStoredResult(pr, "math");
  // MA-301: mirror useTodaysMission.ts's reading-strand bias so the
  // displayed starting skill matches what actually gets served (the
  // priors-driven base can land on a reading skill even though the
  // no-priors fallback below stays math-subject).
  const readingPr = app?.progress?.readingPlacementResults as
    | { accuracy?: number; total?: number }
    | null;
  const readingPlacement = placementFromStoredResult(readingPr, "reading");
  const startingSkillId = recommendStartingSkill({
    priors: app?.progress?.parentPriors,
    // MA-80: same resolved age useTodaysMission passes (displayed === served).
    ageYears: resolveSelectionAge(app?.progress?.studentAge, activeChildAge(app)),
    placement,
    readingPlacement,
    // C-1: the onboarding diagnostic is math, so the no-priors age fallback
    // should seed within MATH (not a reading skill) for the empty 6.5-7.0
    // math band.
    subject: "math",
  });
  const startingSkillLabel = skillLabel(startingSkillId);
  // MA-889 — no label is invented: a skill without a Journey label gets no
  // second line.
  const startingKidLabel = KID_LABEL_MAP[startingSkillId] ?? null;

  // MA-856 — false until the grown-up completes the hand-back hold. The
  // results subtree stays mounted but `hidden` (display:none, out of the
  // accessibility tree) until then, so the MA-80 / MA-301 displayed === served
  // starting-skill derivation keeps rendering exactly as before.
  const [handedBack, setHandedBack] = useState(false);
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);
  // MA-856 RED M1 — the hold completes while the finger is still down, so on
  // touch the release's synthetic click lands on the finish button that now
  // sits under it. A pointer click on finish counts only if that pointer went
  // down inside the revealed results; a keyboard click (detail 0) always counts.
  const pressedInResultsRef = useRef(false);
  const { triggerProps: holdProps, holding, progress: holdFill } = useHoldToOpen(() =>
    setHandedBack(true),
  );
  useEffect(() => {
    if (!handedBack) return;
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    resultsHeadingRef.current?.focus();
  }, [handedBack]);
  // MA-311 — opt-in, never pre-checked: a fresh parent has no notif_pref row
  // yet, which the real Settings toggle (ParentSettingsSections) reads as
  // OFF. Defaulting this ON here would show a contradicting state the
  // moment the parent opens Settings.
  // MA-889 (D-PUX-28) — the "Daily reminder push" row (MA-337's static Soon
  // row) is gone: no reminder exists (no column, no cron), so neither this
  // screen nor Settings offers one until it does.
  const [emailRecap, setEmailRecap] = useState(false);
  const [busy, setBusy] = useState(false);

  // MA-580 path: read at render so the button can say where it goes.
  const claimPending = !!readPendingClaimCode();

  const finish = async (e?: { detail?: number }) => {
    if (e && e.detail !== 0 && !pressedInResultsRef.current) return; // stray release from the hand-back hold
    if (!account || !kid) return;
    setBusy(true);
    // MA-311 — persist the real recap choice to notif_pref (the same row
    // the Settings toggle reads/writes). Non-blocking: setEmailWeeklyRecap
    // never throws (it reports failures to Sentry internally and resolves
    // {ok:false}), so onboarding completion is never held up by this write.
    void setEmailWeeklyRecap(emailRecap);
    // De-mock (PR #15 / MA-31): account, notif_prefs and kids are all
    // useMemo derivations (or stubs), not react-query producers, so the
    // invalidations that lived here were pure no-ops — removed. Completion
    // just routes to /play; the engine owns mastery from child_attempts.
    // MA-580 (A) — ⭐THE CONVERSION MOMENT WAS ROUTED PAST ITS OWN LANDING PAD.
    // A parent who scanned a backpack-note QR arrives at /claim/:code, which
    // stashes the code and sends them to sign up. Onboarding then ended HERE,
    // unconditionally at /play — but `PendingClaimCodeCard`, the ONLY component
    // that reads that code, renders solely on /parent/ behind ParentGate. So the
    // code sat in sessionStorage while the flow's previous step ("Pass the iPad
    // to your kid... no helping!") handed the device to a child for a diagnostic.
    //
    // MA-291's own acceptance is "lands on a parent dash whose FIRST render shows
    // the linked kid -- WITHOUT ever visiting settings." That was unreachable:
    // the auto-redeem at PendingClaimCodeCard fires only if the parent
    // independently returns, passes the parent gate, and reaches /parent/ in the
    // SAME TAB before sessionStorage is gone.
    //
    // Routing on the pending code (rather than always) keeps the ordinary
    // no-code onboarding terminus byte-identical at /play.
    navigate({ to: readPendingClaimCode() ? "/parent" : "/play" });
  };

  if (!kid) {
    return (
      <div className="grid min-h-screen place-items-center bg-cream">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      {!handedBack && (
        <section
          data-testid="handback-step"
          className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-10 text-center"
        >
          <p className={EYEBROW}>Grown-ups</p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            Grown-up, take the device back
          </h1>
          <HandBackPicture />
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Hold the button to see where {kid.name} is starting.
          </p>
          <div className="relative mt-6 w-full">
            <button
              type="button"
              {...holdProps}
              data-testid="handback-hold"
              aria-label={`Hold to continue to ${kid.name}'s results`}
              className={`${HOLD_CTA} relative touch-none select-none overflow-hidden`}
            >
              {holding && (
                <span
                  aria-hidden="true"
                  data-testid="handback-fill"
                  className="pointer-events-none absolute inset-y-0 left-0 bg-primary-pressed"
                  style={{ width: `${Math.round(holdFill * 100)}%` }}
                />
              )}
              <span className="relative">Hold to continue</span>
            </button>
            {holding && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink/80 px-3 py-1 text-xs font-bold text-cream"
              >
                Keep holding&hellip;
              </span>
            )}
          </div>
        </section>
      )}
      <div
        hidden={!handedBack}
        onPointerDownCapture={() => {
          pressedInResultsRef.current = true;
        }}
        data-testid="parent-results"
        className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10"
      >
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>All set</p>
          <h1
            ref={resultsHeadingRef}
            tabIndex={-1}
            className="mt-2 font-serif text-3xl font-bold tracking-tight outline-none"
          >
            Here's where {kid.name} is starting.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Based on what you told us, we&apos;re starting {kid.name} at{" "}
            <b className="text-foreground">{startingSkillLabel}</b>. We&apos;ll
            fine-tune the level as {kid.name} plays.
          </p>
          {startingKidLabel && (
            <p
              data-testid="starting-kid-label"
              className="mt-1 text-sm leading-relaxed text-muted-foreground"
            >
              {kid.name} sees: {startingKidLabel}
            </p>
          )}
        </header>

        <div className="mt-5 rounded-3xl border border-border/60 bg-card p-5 shadow-soft">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Stay in the loop
          </p>
          <Toggle
            label="Weekly recap email"
            sub="What they practiced + standout moments"
            on={emailRecap}
            onChange={setEmailRecap}
          />
        </div>

        <button
          onClick={(e) => void finish(e)}
          disabled={busy}
          data-testid="finish-onboarding"
          className={`mt-6 ${CTA}`}
        >
          {busy
            ? "Saving…"
            : claimPending
              ? "Go to the grown-ups area →"
              : `Start ${kid.name}'s first session →`}
        </button>
        <p
          data-testid="grownups-pointer"
          className="mt-3 text-center text-xs leading-relaxed text-muted-foreground"
        >
          Later, hold the ⚙ at the top of any kid screen to open the grown-ups
          area.
        </p>
      </div>
    </div>
  );
}

/** MA-856 — original inline picture for the hand-back step: a small figure
 *  passes a tablet along an arrow to a taller grown-up. Plain shapes, theme
 *  tokens via currentColor/classes; no external or third-party art. */
function HandBackPicture() {
  return (
    <svg
      role="img"
      aria-label="A child handing a tablet to a grown-up"
      data-testid="handback-picture"
      viewBox="0 0 240 150"
      className="mt-6 h-36 w-full max-w-[260px] text-ink"
    >
      {/* child (left, small) */}
      <circle cx="46" cy="70" r="13" className="fill-terracotta-deep/80" />
      <path d="M30 140 V104 a16 16 0 0 1 32 0 V140 Z" className="fill-terracotta-deep/60" />
      <path d="M58 102 L84 92" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" />
      {/* tablet in hand-off */}
      <rect x="92" y="70" width="40" height="30" rx="5" className="fill-card" stroke="currentColor" strokeWidth="3" />
      <rect x="98" y="76" width="28" height="18" rx="2" className="fill-primary/30" />
      {/* arrow toward the grown-up */}
      <path d="M100 56 Q120 40 146 50" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="5 5" />
      <path d="M140 42 L148 51 L137 55" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* grown-up (right, tall) */}
      <circle cx="190" cy="40" r="15" className="fill-primary/80" />
      <path d="M170 140 V80 a20 20 0 0 1 40 0 V140 Z" className="fill-primary/60" />
      <path d="M172 88 L140 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function Toggle({
  label,
  sub,
  on,
  onChange,
}: {
  label: string;
  sub: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className="mt-3 flex min-h-[44px] w-full items-start justify-between gap-3 text-left"
    >
      <div>
        <p className="text-sm font-bold">{label}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      <span
        className={`relative mt-1 inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
          on ? "bg-primary" : "bg-ink/25"
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition-transform ${
            on ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}
